import React, { useState, useEffect, useContext } from 'react';
import useGoogleApi, {
    AuthenticationState,
    ErrorType as AuthErrorType,
} from 'PlugAndPlay/_global/jsx/integrations/google/useGoogleApi.jsx';
import GoogleAuthScopeContext from '../../../_global/jsx/integrations/google/GoogleAuthScopeContext';
import EmailsListingContext, {
    EmailsListingContextErrorType as ErrorType,
} from './emails-listing-context';
import decodeHtml from '../../../_global/js/sanitise';

export const SESSION_STORAGE_ID = 'GmailList';
export const LOCAL_SCOPE_STORAGE_ID = 'GmailListScopeApproved';

export default ({ apiKey, clientId, mailboxLink, children }) => {
    // In order to not show the 'please approve these scopes' popup each time we need to know
    // if a user has approved this before; this might be from localstorage, matrix, datastore
    const { hasScopeBeenApproved, setHasScopeBeenApproved } = useContext(
        GoogleAuthScopeContext
    );

    const {
        authenticationState,
        authenticate,
        retryAuthentication,
        checkTokenState,
        loginButton,
        errorType: authErrorType,
    } = useGoogleApi({
        discoveryDocs: [
            'https://www.googleapis.com/discovery/v1/apis/gmail/v1/rest',
        ],
        scope: 'https://www.googleapis.com/auth/gmail.readonly',
        apiKey,
        clientId,
        tokenStorageId: SESSION_STORAGE_ID,
    });

    const [emails, setEmails] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Convert useGoogleApi error to local error type
    useEffect(() => {
        if (authErrorType !== null && authErrorType) {
            if (authErrorType === AuthErrorType.AUTH_ERROR) {
                setError(ErrorType.AUTH_ERROR);
            } else if (authErrorType === AuthErrorType.SCOPE_ERROR) {
                setError(ErrorType.SCOPE_ERROR);
            } else {
                setError(ErrorType.ERROR);
            }
        }
    }, [authErrorType]);

    useEffect(() => {
        // Once Google API connector is ready to start run it
        if (
            authenticationState === AuthenticationState.READY_FOR_AUTH ||
            authenticationState === AuthenticationState.NEED_REFRESH
        ) {
            // Default is null, will be true / false once it has been retrieved
            if (hasScopeBeenApproved !== null) {
                authenticate({
                    userHasApprovedScope: hasScopeBeenApproved,
                });
            }
        }

        // Once authenticated update the scope approval if needed
        if (authenticationState === AuthenticationState.AUTHENTICATED) {
            if (!hasScopeBeenApproved) {
                setHasScopeBeenApproved(true);
            }
        }
    }, [hasScopeBeenApproved, authenticationState]);

    const getEmails = ({ count }) => {
        setLoading(true);

        const getMessagesPromise = window.gapi.client.gmail.users.threads.list({
            userId: 'me',
            maxResults: count,
        });

        const getMessagePromise = (id) =>
            window.gapi.client.gmail.users.threads.get({ userId: 'me', id });

        checkTokenState(
            getMessagesPromise
                .then((response) =>
                    Promise.all(
                        response.result.threads.map(({ id }) =>
                            getMessagePromise(id)
                        )
                    )
                )
                .catch((e) => {
                    setError(e);
                    setLoading(false);
                })
                .then((response) => {
                    const myEmails = response.map(({ result }) => {
                        // Getting latest email from thread.
                        const lastMessage = result.messages.pop();
                        const {
                            id,
                            snippet,
                            labelIds,
                            payload: { headers },
                        } = lastMessage;

                        const title =
                            headers[
                                headers.findIndex((el) => el.name === 'Subject')
                            ]?.value;
                        // Getting from header and removing email pattern <email@test.com>
                        const from = headers[
                            headers.findIndex((el) => el.name === 'From')
                        ]?.value.replace(/<.*/i, '');
                        const isRead =
                            labelIds.findIndex((el) => el === 'UNREAD') > -1;

                        return {
                            id,
                            from,
                            title,
                            isRead,
                            link: `https://mail.google.com/mail/#inbox/${id}`,
                            subtitle: decodeHtml(snippet),
                        };
                    });

                    setEmails(myEmails);
                    setLoading(false);
                })
                .catch((e) => {
                    setError(e);
                    setLoading(false);
                })
        );
    };

    const restartAuthProcess = () => {
        setError(null);
        retryAuthentication({
            userHasApprovedScope: hasScopeBeenApproved,
        });
    };

    return (
        <EmailsListingContext.Provider
            value={{
                isLoading:
                    authenticationState === AuthenticationState.NOT_STARTED ||
                    authenticationState === AuthenticationState.READY_FOR_AUTH
                        ? true
                        : isLoading,
                isReady:
                    authenticationState === AuthenticationState.AUTHENTICATED,
                isAuthNeeded:
                    authenticationState ===
                    AuthenticationState.NEEDS_LOGIN_CONSENT,
                signInLink: loginButton,
                error,
                getEmails,
                emails,
                restartAuthProcess,
                mailboxLink:
                    mailboxLink || 'https://mail.google.com/mail/#inbox/',
            }}
        >
            {children}
        </EmailsListingContext.Provider>
    );
};
